/**
 * Which incomplete setup blocks which route family.
 *
 * The onboarding state's `missingRequirements` is the source of truth for what a user still owes;
 * this module decides, per destination, which of those items are *fundamental* enough to block
 * entry. It is deliberately NOT a universal "accountReady gate": a user missing availability can
 * still reach settings, billing, and integrations to fix things — only the scheduling surfaces
 * (`/dashboard/teams`, `/create-event`) are held.
 *
 * `missingRequirements` already models every exemption server-side (an invitee's array never
 * contains `"calendar"`, a team member's never contains `"firstEvent"`), so intersecting a route's
 * `blockedBy` with it cannot bounce a user to a step they are exempt from.
 */

/**
 * Keyed global-activity handle bridging ANY redirect into /onboarding — the welcome first run
 * (status NOT_STARTED) and the focused setup-restore flow (?setup=1). FirstRunGate or
 * AcceptInvitationPage starts it the moment it decides to redirect; OnboardingPage ends it once
 * its screen is painted. Without the bridge the user sees a dashboard skeleton, then a bare frame
 * during the route swap, then the onboarding spinner — three separate loading visuals. With it,
 * GlobalApiLoader stays up continuously.
 */
export const ONBOARDING_ENTRY_ACTIVITY = "onboarding-entry-redirect";

export interface RouteSetupPolicyEntry {
  /** Pathname prefix this entry governs. Longest match wins. */
  prefix: string;
  /** Missing-requirement keys that block this route family. Empty ⇒ never blocked. */
  blockedBy: string[];
}

/**
 * Ordered by specificity is not required — `policyFor` picks the longest matching prefix — but
 * keeping related prefixes together aids reading. Anything with no entry here is never gated.
 */
export const ROUTE_SETUP_POLICY: RouteSetupPolicyEntry[] = [
  { prefix: "/dashboard/teams", blockedBy: ["availability", "calendar"] },
  { prefix: "/create-event", blockedBy: ["availability", "calendar"] },
  // No entry for "/dashboard" root, "/dashboard/settings", "/dashboard/billing",
  // "/dashboard/integrations", … — those stay reachable while setup is incomplete.
];

/** Longest-prefix match for a pathname, or null when no policy governs it. */
export function policyFor(pathname: string): RouteSetupPolicyEntry | null {
  let best: RouteSetupPolicyEntry | null = null;
  for (const entry of ROUTE_SETUP_POLICY) {
    const matches = pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`);
    if (matches && (best === null || entry.prefix.length > best.prefix.length)) {
      best = entry;
    }
  }
  return best;
}

/**
 * The requirements that both (a) block `pathname` and (b) the user is actually missing.
 * Empty ⇒ the route is not gated for this user. Order follows the route's `blockedBy`.
 */
export function getBlockingRequirements(pathname: string, missingRequirements: readonly string[]): string[] {
  const policy = policyFor(pathname);
  if (!policy) return [];
  const missing = new Set(missingRequirements);
  return policy.blockedBy.filter((req) => missing.has(req));
}
